import { useApolloClient } from '@apollo/client';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import {
  every,
  get,
  initial,
  last,
  map,
  merge,
  noop,
  pick,
  set,
  some,
  startsWith,
} from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { formToInputAddress } from '../../../app/data/locationConversions';
import { NewShipmentDateSpecialValue } from '../../../app/data/newShipmentConversions';
import {
  CURRENT_TIME_QUERY,
  IS_HOLIDAY,
} from '../../../app/graphql/geographicQueries';
import { fromServerDateTime } from '../../../common/utils/dateUtils';
import { useDialogControls } from '../../../common/utils/dialogUtils';
import { useCachedProp } from '../../../common/utils/hookUtils';
import Whitespace from '../../../components/Whitespace';
import { FAIcon } from '../../../components/adapters/fontAwesomeAdapters';
import { SimpleConfirmDialog } from '../../../components/dialogs/SimpleConfirmDialog';
import { FormItemFieldRegistration } from '../../../components/forms/basicFormElements';
import { FormItemDateTimePicker } from '../../../components/forms/dateElements';
import { useDateElementsStyles } from '../../../components/forms/dateElements.styles';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import { useDynamicFormSchemaFieldDefinition } from '../../../components/forms/dynamic/dynamicFormSchema';
import { useDynamicFormValidationRules } from '../../../components/forms/dynamic/dynamicFormValidation';
import {
  isFormValuePresent,
  mergeFormNames,
  useValidationAfterChange,
} from '../../../components/forms/formHelpers';
import { useFormContext } from '../../../components/forms/forms';
import { nextEventPromise } from '../../../utils/htmlUtils';
import { SavedShipmentContext } from '../newShipment/SavedShipmentProvider';
import { JobLocationSection } from '../newShipment/newShipmentElements';
import HolidayWarningDialog from './HolidayWarningDialog';
import { useTooltipStyles } from '../../../styles/tooltip.styles';

const getNameWithSuffix = (name, suffix) =>
  mergeFormNames(initial(name), `${last(name)}-${suffix}`);

const getSpecialValueNames = name => ({
  flag: getNameWithSuffix(name, 'specialValue'),
  data: getNameWithSuffix(name, 'specialValueData'),
});

function useDateTimeSpecialValue({ name }) {
  const dateName = getNameWithSuffix(name, 'date');
  const timeName = getNameWithSuffix(name, 'time');
  const { values, formInstance, forceUpdate } = useFormContext();

  const { flag: specialValueName, data: specialValueDataName } =
    getSpecialValueNames(name);

  // Validation rules change depending on the value, so it needs to be rerun on change
  useValidationAfterChange({
    dependencyName: specialValueName,
    dependentNames: [name, dateName, timeName],
  });

  const date = get(values, dateName)?.toISOString();
  const specialValue = get(values, specialValueName);

  useEffect(() => {
    if (date && specialValue) {
      // Disabling special value when a date is selected
      const newVals = {};
      set(newVals, specialValueName, null);
      set(newVals, specialValueDataName, null);
      formInstance.setFieldsValue(newVals);
      forceUpdate();
    }
  }, [
    date,
    forceUpdate,
    formInstance,
    specialValue,
    specialValueDataName,
    specialValueName,
  ]);

  return {
    specialValue,
    setSpecialValue: value => {
      const newVals = {};
      set(newVals, specialValueName, value);
      set(newVals, specialValueDataName, null);
      set(newVals, name, null);
      set(newVals, dateName, null);
      set(newVals, timeName, null);
      formInstance.setFieldsValue(newVals);
      forceUpdate();
    },
  };
}

function computePropsFromDateTimeSpecialValue(
  value,
  { rules, specialRules, datePickerProps, intl }
) {
  if (value) {
    let placeholder = '';

    if (value === NewShipmentDateSpecialValue.NOW) {
      placeholder = intl.formatMessage({
        id: 'book.newShipment.label.dateTime.now',
      });
    }
    if (value === NewShipmentDateSpecialValue.ASAP) {
      placeholder = intl.formatMessage({ id: 'date.asap' });
    }
    if (value === NewShipmentDateSpecialValue.TBD) {
      placeholder = intl.formatMessage({ id: 'labels.tbd.saveShipment' });
    }

    return {
      rules: [{ required: false }, ...specialRules],
      timeProps: { disabled: true },
      dateProps: {
        formItemComponentProps: {
          className: classNames(
            datePickerProps.dateProps.formItemComponentProps.className,
            'ant-picker--special-value'
          ),
          placeholder,
        },
      },
    };
  }
  return { rules };
}

const NOW_DEPENDENCIES_FORM = [
  ['address', 'country'],
  ['address', 'stateProvince'],
  ['address', 'city'],
];
const NOW_DEPENDENCIES_VALIDATION = ['country', 'stateProvince', 'city'];

const HOLIDAY_DEPENDENCIES_FORM = [['address', 'country']];
const HOLIDAY_DEPENDENCIES_VALIDATION = ['country'];

const DEFAULT_GET_ADDRESS = ({ formValues }) => formValues.address;
function useNowValidator({
  name,
  getAddress = DEFAULT_GET_ADDRESS,
  onError: onErrorOuter = noop,
  nowDependenciesForm,
}) {
  const { formInstance, forceUpdate, submittingRef } = useFormContext();
  const client = useApolloClient();
  const { flag: specialValueName, data: specialValueDataName } =
    getSpecialValueNames(name);

  const [nowAvailable, setNowAvailable] = useState(true);

  const getCompactAddress = vals =>
    pick(getAddress({ formValues: vals }), NOW_DEPENDENCIES_VALIDATION);
  const deps = useCachedProp(getCompactAddress(formInstance.getFieldsValue()));
  useEffect(() => {
    setNowAvailable(true);
  }, [deps]);

  const onError = () => {
    setNowAvailable(false);
    formInstance.setFieldsValue({
      [specialValueName]: null,
      [specialValueDataName]: null,
    });
    forceUpdate();
    onErrorOuter();
  };

  const rule = {
    validator: async () => {
      if (
        formInstance.getFieldValue(specialValueName) !==
        NewShipmentDateSpecialValue.NOW
      ) {
        return Promise.resolve();
      }

      // Wait for editing of dependencies to be finished
      const { activeElement } = document;
      if (
        activeElement &&
        some(nowDependenciesForm, dep =>
          startsWith(activeElement.id, dep.join(','))
        )
      ) {
        await nextEventPromise(activeElement, 'blur');
      }

      const addr = getCompactAddress(formInstance.getFieldsValue());
      // Skip if one of the dependencies is empty (unless submitting, then we will check anyway)
      if (
        !every(NOW_DEPENDENCIES_VALIDATION, fieldName =>
          isFormValuePresent(addr[fieldName])
        ) &&
        !submittingRef.current
      ) {
        return Promise.resolve();
      }

      try {
        const {
          data: { currentTime },
        } = await client.query({
          query: CURRENT_TIME_QUERY,
          variables: {
            address: formToInputAddress(addr),
          },
          fetchPolicy: 'network-only',
        });
        if (!currentTime) {
          onError();
          return Promise.reject();
        }

        const newVals = {};
        set(newVals, specialValueDataName, fromServerDateTime(currentTime));
        formInstance.setFieldsValue(newVals);
        forceUpdate();
        return Promise.resolve();
      } catch (e) {
        onError();
        return Promise.reject();
      }
    },
  };

  return { rule, nowAvailable };
}

function useHolidayValidator({
  name,
  getAddress = DEFAULT_GET_ADDRESS,
  onError = noop,
  dependencies,
  hasAccepetedHoliday = false,
}) {
  const { formInstance, submittingRef } = useFormContext();
  const client = useApolloClient();

  const getCompactAddress = vals =>
    pick(getAddress({ formValues: vals }), HOLIDAY_DEPENDENCIES_VALIDATION);

  const rule = {
    validator: async (_rule, value) => {
      if (hasAccepetedHoliday) {
        return Promise.resolve();
      }

      const formValues = formInstance.getFieldsValue();
      const specialValue = get(
        formValues,
        getNameWithSuffix(name, 'specialValue')
      );
      const isASAP = specialValue === NewShipmentDateSpecialValue.ASAP;
      const isNow = specialValue === NewShipmentDateSpecialValue.NOW;
      const isTBD = specialValue === NewShipmentDateSpecialValue.TBD;

      if (isASAP || isNow || isTBD) {
        return Promise.resolve();
      }

      // Wait for editing of dependencies to be finished
      const { activeElement } = document;
      if (
        activeElement &&
        some(dependencies, dep => startsWith(activeElement.id, dep.join(',')))
      ) {
        await nextEventPromise(activeElement, 'blur');
      }

      const addr = getCompactAddress(formValues);
      // Skip if one of the dependencies is empty (unless submitting, then we will check anyway)
      if (
        !every(HOLIDAY_DEPENDENCIES_VALIDATION, fieldName =>
          isFormValuePresent(addr[fieldName])
        ) &&
        !submittingRef.current
      ) {
        return Promise.resolve();
      }

      try {
        const {
          data: { isHoliday },
        } = await client.query({
          query: IS_HOLIDAY,
          variables: {
            input: {
              country: addr.country,
              date: value?.format('YYYY-MM-DD'),
            },
          },
        });

        if (isHoliday?.isHoliday === 'true') {
          onError();
          return Promise.reject();
        }

        return Promise.resolve();
      } catch (e) {
        return Promise.resolve();
      }
    },
  };

  return { rule };
}

function DateTimeSpecialValueButton({ onClick, textId }) {
  const { styles } = useDateElementsStyles();
  return (
    <div className={styles.footerButton} onClick={onClick}>
      <FormattedMessage id={textId} />
    </div>
  );
}

function DateTimePickerRow({
  name,
  schemaName,
  dateLabelId,
  timeLabelId = 'book.newShipment.label.time',
  timeTooltipId = 'book.newShipment.label.time.tooltip',
  showNow,
  showAsap,
  showTbd,
  requiredInLabel,
  getAddress,
  addressSchemaPrefix,
  dateInputProps,
  validateHoliday,
}) {
  const { styles } = useTooltipStyles();
  const { styles: dateStyles } = useDateElementsStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const intl = useIntl();
  const { specialValue, setSpecialValue } = useDateTimeSpecialValue({ name });
  const [acceptedHoliday, setAcceptedHoliday] = useState(false);
  const { formInstance } = useFormContext();

  const fieldDef = useDynamicFormSchemaFieldDefinition({
    schemaName,
  });
  const rules = useDynamicFormValidationRules({
    schemaName,
    validation: fieldDef.validation,
  });

  const { flag: specialValueName, data: specialValueDataName } =
    getSpecialValueNames(name);

  const nowErrorDialog = useDialogControls();
  const holidayErrorDialog = useDialogControls();
  const prefixedDependeciesForm = addressSchemaPrefix
    ? map(NOW_DEPENDENCIES_FORM, dep => [addressSchemaPrefix, ...dep])
    : NOW_DEPENDENCIES_FORM;
  const { rule: nowRule, nowAvailable } = useNowValidator({
    name,
    onError: nowErrorDialog.open,
    getAddress,
    nowDependenciesForm: prefixedDependeciesForm,
  });

  const prefixedHolidayDependeciesForm = addressSchemaPrefix
    ? map(HOLIDAY_DEPENDENCIES_FORM, dep => [addressSchemaPrefix, ...dep])
    : HOLIDAY_DEPENDENCIES_FORM;
  const { rule: holidayRule } = useHolidayValidator({
    name,
    onError: holidayErrorDialog.open,
    getAddress,
    dependencies: prefixedHolidayDependeciesForm,
    hasAccepetedHoliday: acceptedHoliday,
  });
  // Ant's `dependencies` are not triggering validation after the programmatic changes,
  // which is insufficient for our scenario with lot of autofills
  useValidationAfterChange({
    dependenciesNames: showNow
      ? prefixedDependeciesForm
      : prefixedHolidayDependeciesForm,
    dependentNames: [name],
  });

  const pickerPropsStatic = {
    dateProps: {
      rules: validateHoliday ? [holidayRule] : [],
      validateFirst: true,
      formItemComponentProps: {
        ...dateInputProps,
        onChange: (date, dateString) => {
          setAcceptedHoliday(false);
          dateInputProps?.onChange?.(date, dateString);
        },
        renderExtraFooter: ({ setPickerOpen }) => (
          <>
            {showNow && nowAvailable && (
              <DateTimeSpecialValueButton
                textId="date.now"
                onClick={() => {
                  setSpecialValue(NewShipmentDateSpecialValue.NOW);
                  setTimeout(() => setPickerOpen(false), 0);
                }}
              />
            )}
            {showAsap && (
              <DateTimeSpecialValueButton
                textId="date.asap"
                onClick={() => {
                  setSpecialValue(NewShipmentDateSpecialValue.ASAP);
                  setTimeout(() => setPickerOpen(false), 0);
                }}
              />
            )}
            {showTbd && (
              <DateTimeSpecialValueButton
                textId="labels.tbd.saveShipment"
                onClick={() => {
                  setSpecialValue(NewShipmentDateSpecialValue.TBD);
                  setTimeout(() => setPickerOpen(false), 0);
                }}
              />
            )}
          </>
        ),
      },
      showNow: false,
    },
  };
  const pickerProps = merge(
    {},
    pickerPropsStatic,
    computePropsFromDateTimeSpecialValue(specialValue, {
      rules,
      specialRules: showNow ? [nowRule] : [],
      datePickerProps: pickerPropsStatic,
      intl,
    })
  );

  return (
    <JobLocationSection
      title={
        <div
          className={classNames(
            'DateTimePicker expand-size',
            dateStyles.dateTimePicker
          )}
        >
          <div className="Col-DatePicker">
            <FormattedMessage id={dateLabelId} />
            {requiredInLabel && ' *'}
          </div>
          <div className="Col-TimePicker text-no-transform hide-md-and-smaller">
            <FormattedMessage id={timeLabelId} />
            {requiredInLabel && ' *'}
            {timeTooltipId && (
              <>
                <Whitespace />
                <Tooltip
                  title={<FormattedMessage id={timeTooltipId} />}
                  classNames={{ root: styles.customLines }}
                >
                  <FAIcon
                    icon="question-circle"
                    className="color-highlight icon-12"
                  />
                </Tooltip>
              </>
            )}
          </div>
        </div>
      }
      titleClassName={layoutStyles.flex1}
    >
      <div>
        <FormItemDateTimePicker
          name={name}
          rules={rules}
          expandSize
          {...pickerProps}
        />
        <FormItemFieldRegistration name={specialValueName} />
        <FormItemFieldRegistration name={specialValueDataName} />
        <SimpleConfirmDialog
          open={nowErrorDialog.isOpen}
          onClose={nowErrorDialog.close}
          cancelTextId="buttons.close"
          textId="book.newShipment.label.dateTime.now.notAvailable"
        />
        <HolidayWarningDialog
          isOpen={holidayErrorDialog.isOpen}
          onOk={() => {
            setAcceptedHoliday(true);
            formInstance.setFields([
              {
                name: getNameWithSuffix(name, 'date'),
                errors: [],
              },
            ]);
            holidayErrorDialog.close();
          }}
        />
      </div>
    </JobLocationSection>
  );
}

const PICKUP_DT_NAME = 'pickupDateTime';
export function PickupDateTimeField({
  labelId = 'book.newShipment.label.pickupDateTime',
  showRequiredInLabel,
  getAddress,
  addressSchemaPrefix,
  dateInputProps,
  validateHoliday = true,
  namePrefix,
}) {
  const fieldDef = useDynamicFormSchemaFieldDefinition({
    schemaName: PICKUP_DT_NAME,
  });
  const { enabled: savedShipmentEnabled } = useContext(SavedShipmentContext);

  return (
    <DateTimePickerRow
      name={mergeFormNames(namePrefix, PICKUP_DT_NAME)}
      schemaName={PICKUP_DT_NAME}
      dateLabelId={labelId}
      showNow
      showTbd={savedShipmentEnabled}
      requiredInLabel={
        showRequiredInLabel && fieldDef.validation.rules.required
      }
      getAddress={getAddress}
      addressSchemaPrefix={addressSchemaPrefix}
      dateInputProps={dateInputProps}
      validateHoliday={validateHoliday}
    />
  );
}

const DELIVERY_DT_NAME = 'deliveryDateTime';
export function DeliveryDateTimeField({
  labelId = 'book.newShipment.label.deliveryDateTime',
  showRequiredInLabel,
  getAddress,
  validateHoliday = true,
  namePrefix,
}) {
  const fieldDef = useDynamicFormSchemaFieldDefinition({
    schemaName: PICKUP_DT_NAME,
  });
  const { enabled: savedShipmentEnabled } = useContext(SavedShipmentContext);

  return (
    <DateTimePickerRow
      name={mergeFormNames(namePrefix, DELIVERY_DT_NAME)}
      schemaName={DELIVERY_DT_NAME}
      dateLabelId={labelId}
      showNow={false}
      showAsap
      showTbd={savedShipmentEnabled}
      getAddress={getAddress}
      requiredInLabel={
        showRequiredInLabel && fieldDef.validation.rules.required
      }
      validateHoliday={validateHoliday}
    />
  );
}
